import { useState } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { ShareDialog } from '@/components/sharing/ShareDialog';
import { toast } from 'sonner';
import { lc } from '@/i18n/copy';

interface PostPublishSuccessCardProps {
  onDismiss: () => void;
  publicSlug?: string | null;
  recipientName?: string | null;
}

const copy = {
  en: {
    title: '💚 You showed up.',
    body: 'Some people discover Say It NOW because someone shared something meaningful with them.',
    secondary: "If there's someone you think this could help, you can share it — quietly.",
    shareAction: 'Share your message',
    sharePlatform: 'Share Say It NOW',
    dismissAction: 'Not now',
    shareText: 'A private space for saying what matters.',
    linkCopied: 'Link copied',
  },
  es: {
    title: '💚 Te presentaste.',
    body: 'Algunas personas descubren Say It NOW porque alguien compartió algo significativo con ellas.',
    secondary: 'Si hay alguien a quien crees que esto podría ayudar, puedes compartirlo — discretamente.',
    shareAction: 'Comparte tu mensaje',
    sharePlatform: 'Compartir Say It NOW',
    dismissAction: 'Ahora no',
    shareText: 'Un espacio privado para decir lo que importa.',
    linkCopied: 'Enlace copiado',
  },
  fr: {
    title: '💚 Vous vous êtes montré.',
    body: 'Certaines personnes découvrent Say It NOW parce que quelqu\'un a partagé quelque chose de significatif avec elles.',
    secondary: 'Si vous pensez que cela pourrait aider quelqu\'un, vous pouvez le partager — discrètement.',
    shareAction: 'Partager votre message',
    sharePlatform: 'Partager Say It NOW',
    dismissAction: 'Pas maintenant',
    shareText: 'Un espace privé pour dire ce qui compte.',
    linkCopied: 'Lien copié',
  },
  pt: {
    title: '💚 Você apareceu.',
    body: 'Algumas pessoas descobrem o Say It NOW porque alguém compartilhou algo significativo com elas.',
    secondary: 'Se há alguém que você acha que isso poderia ajudar, pode compartilhar — discretamente.',
    shareAction: 'Compartilhar sua mensagem',
    sharePlatform: 'Compartilhar Say It NOW',
    dismissAction: 'Agora não',
    shareText: 'Um espaço privado para dizer o que importa.',
    linkCopied: 'Link copiado',
  },
  ar: {
    title: '💚 لقد حضرت.',
    body: 'بعض الناس يكتشفون Say It NOW لأن شخصاً ما شارك شيئاً ذا معنى معهم.',
    secondary: 'إذا كان هناك شخص تعتقد أن هذا قد يساعده، يمكنك مشاركته — بهدوء.',
    shareAction: 'مشاركة رسالتك',
    sharePlatform: 'مشاركة Say It NOW',
    dismissAction: 'ليس الآن',
    shareText: 'مساحة خاصة لقول ما يهم.',
    linkCopied: 'تم نسخ الرابط',
  },
  ja: {
    title: '💚 あなたは誰かのために現れました。',
    body: 'Say It NOWを知る人の中には、誰かが意味のあるものを共有してくれたからという人もいます。',
    secondary: 'これが役立つかもしれない人がいれば、静かに共有できます。',
    shareAction: 'メッセージを共有',
    sharePlatform: 'Say It NOWを共有',
    dismissAction: '今はしない',
    shareText: '大切なことを伝えるためのプライベートな空間。',
    linkCopied: 'リンクをコピーしました',
  },
  ko: {
    title: '💚 당신이 나타났습니다.',
    body: '어떤 사람들은 누군가가 의미 있는 것을 공유해 주어서 Say It NOW를 알게 됩니다.',
    secondary: '이것이 도움이 될 수 있는 사람이 있다면, 조용히 공유할 수 있습니다.',
    shareAction: '메시지 공유',
    sharePlatform: 'Say It NOW 공유',
    dismissAction: '지금은 아니요',
    shareText: '중요한 것을 말하기 위한 프라이빗 공간.',
    linkCopied: '링크 복사됨',
  },
  id: {
    title: '💚 Anda hadir.',
    body: 'Beberapa orang menemukan Say It NOW karena seseorang berbagi sesuatu yang bermakna dengan mereka.',
    secondary: 'Jika ada seseorang yang menurut Anda ini bisa membantu, Anda bisa membagikannya — dengan tenang.',
    shareAction: 'Bagikan pesan Anda',
    sharePlatform: 'Bagikan Say It NOW',
    dismissAction: 'Tidak sekarang',
    shareText: 'Ruang pribadi untuk mengatakan apa yang penting.',
    linkCopied: 'Link disalin',
  },

  hi: {
    title: '💚 आप आए।',
    body: 'कुछ लोग Say It NOW को इसलिए खोजते हैं क्योंकि किसी ने उनके साथ कुछ सार्थक शेयर किया था।',
    secondary: "If there's someone you think this could help, you can share it — quietly.",
    shareAction: 'अपना संदेश शेयर करें',
    sharePlatform: 'Say It NOW शेयर करें',
    dismissAction: 'अभी नहीं',
    shareText: 'जो मायने रखता है उसे कहने के लिए एक निजी जगह।',
    linkCopied: 'लिंक कॉपी किया गया',
  },
};

export function PostPublishSuccessCard({ onDismiss, publicSlug, recipientName }: PostPublishSuccessCardProps) {
  const { language } = useLanguage();
  const t = lc(copy, language);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const hasAnnouncementData = publicSlug && recipientName;

  const handleShare = async () => {
    if (hasAnnouncementData) {
      setShareDialogOpen(true);
    } else {
      const shareUrl = window.location.origin;

      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Say It NOW',
            text: t.shareText,
            url: shareUrl,
          });
        } catch (err) {
          if ((err as Error).name !== 'AbortError') {
            await navigator.clipboard.writeText(shareUrl);
            toast.success(t.linkCopied);
          }
        }
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success(t.linkCopied);
      }
    }
  };

  return (
    <>
      <Card className="bg-muted/30 border-border/50">
        <CardContent className="py-6 px-6">
          <p className="text-lg font-medium text-foreground mb-2">
            {t.title}
          </p>
          <p className="text-sm text-muted-foreground mb-1">
            {t.body}
          </p>
          <p className="text-xs text-muted-foreground/70 mb-4">
            {t.secondary}
          </p>
          <div className="flex items-center gap-4">
            <button
              onClick={handleShare}
              className="text-sm text-foreground/80 hover:text-foreground hover:underline transition-colors"
            >
              {hasAnnouncementData ? t.shareAction : t.sharePlatform}
            </button>
            <button
              onClick={onDismiss}
              className="text-sm text-muted-foreground/70 hover:text-muted-foreground transition-colors"
            >
              {t.dismissAction}
            </button>
          </div>
        </CardContent>
      </Card>

      {hasAnnouncementData && (
        <ShareDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          publicSlug={publicSlug}
          recipientName={recipientName}
        />
      )}
    </>
  );
}
